// Creatures aboard (server/aliens.ts): not people, so none of the humanoid figure, the crew model, the name plate, the
// wield, seat and emote poses: each is one copy of its model (assets/enemies/alien.glb, rigged and animated in Blender,
// 2026-10-18), its own skeleton and animation groups, played from what the server says it is doing (PlayerInfo.cact, and
// cn counting the one-shots). Loops (idle, walk, run) cross-fade over a fifth of a second, the gait at the pace it really
// covers ground; the attack and the flinch play to their end; the death plays once and holds. Same approach as Trigger
// (client/pets.ts). main.ts keeps them in the roster and asks this for their bodies (shots, culling, the fog, the marks).
import { Color3, PBRMaterial, SceneLoader, StandardMaterial, TransformNode, Vector3, type AbstractMesh, type AnimationGroup, type AssetContainer, type InstantiatedEntries, type Scene } from "@babylonjs/core";
import { BODY_TO_FEET, type CreatureAct, type CreatureKind, type PlayerInfo } from "../shared/protocol.js";
import { Track } from "./interp.js";

// the model, and how fast each gait covers ground at 1x (m/s), so the feet keep pace with the ground
const MODELS: Record<CreatureKind, { dir: string; file: string; v: number; walk: number; run: number }> = { alien: { dir: "/assets/enemies/", file: "alien.glb", v: 1, walk: 1.6, run: 4.5 } };
const LOOPS = new Set(["idle", "walk", "run"]);
const FADE_S = 0.2, NEAR = 45;
// for the shots (client/range.ts): how big a target it is (round its feet), and where its head starts, crouched as it is
export const CREATURE_HIT = { r: 0.5, h: 1.4, head: 1.05 };
interface Body {
  kind: CreatureKind; root: TransformNode; inst: InstantiatedEntries | null; meshes: AbstractMesh[]; groups: Map<string, AnimationGroup>;
  cur: string; from: AnimationGroup | null; fade: number; target: Vector3; prev: Vector3; yaw: number; targetYaw: number;
  act: CreatureAct; cn: number; played: number; dead: boolean; speed: number; shown: boolean; fogHidden: boolean;
  track: Track;                 // its timed poses: drawn between two of them, as people are (client/interp.ts)
}

export class Creatures {
  private bodies = new Map<string, Body>();
  private models = new Map<CreatureKind, AssetContainer | null>();
  constructor(private scene: Scene) {}
  has(id: string) { return this.bodies.has(id); }
  ids() { return [...this.bodies.keys()]; }
  // where one stands (its feet), for the shots, the marks and the tags; null: not a creature here
  feet(id: string): Vector3 | null { return this.bodies.get(id)?.root.position ?? null; }
  // each body's root, for the culling (main.ts draws it only where people are drawn), and whether it's still on its feet
  each(): IterableIterator<[string, { root: TransformNode; dead: boolean; shown: boolean }]> { return this.bodies.entries(); }
  private model(kind: CreatureKind): AssetContainer | null {
    const m = MODELS[kind];
    if (!this.models.has(kind)) {
      this.models.set(kind, null);
      SceneLoader.LoadAssetContainerAsync(m.dir, `${m.file}?v=${m.v}`, this.scene, undefined, ".glb").then((c) => {
        for (const mt of c.materials) this.tune(mt);
        this.models.set(kind, c);
        for (const [id, b] of this.bodies) if (b.kind === kind && !b.inst) this.dress(id, b, c);
      }).catch((e) => console.warn("creature model", kind, e));
    }
    return this.models.get(kind) ?? null;
  }
  // dark metal with a purple glow: the ship's lights are plain lamps with no environment to reflect, so a fully metallic surface
  // reads black; a little of its own colour glows through, and the metal is eased so the lamps catch it
  private tune(mt: unknown) {
    if (mt instanceof PBRMaterial) { mt.metallic = Math.min(mt.metallic ?? 1, 0.55); mt.roughness = Math.max(mt.roughness ?? 0.5, 0.35); if (mt.albedoTexture) { mt.emissiveTexture = mt.albedoTexture; mt.emissiveColor = new Color3(0.14, 0.02, 0.26); } }   // (checked in the lower hangar's light, 2026-10-18: more reads as purple plastic)
    else if (mt instanceof StandardMaterial) { mt.emissiveColor = new Color3(0.12, 0.05, 0.18); }
  }
  // its model on the body: one copy, its own skeleton and animations
  private dress(id: string, b: Body, c: AssetContainer) {
    const inst = c.instantiateModelsToScene((n) => `cr_${id}_${n}`, false, { doNotInstantiate: true });
    for (const n of inst.rootNodes) n.parent = b.root;
    b.meshes = b.root.getChildMeshes(false); for (const m of b.meshes) { m.isPickable = false; if (b.fogHidden) m.isVisible = false; }
    b.groups = new Map(); inst.animationGroups.forEach((g, i) => { g.stop(); b.groups.set(c.animationGroups[i]?.name ?? g.name, g); });
    b.inst = inst; b.cur = ""; b.from = null; b.fade = 1;
    if (b.dead) { const d = b.groups.get("die"); if (d) { d.start(false, 1, d.from, d.to); d.goToFrame(d.to); b.cur = "die"; } }   // a body already: as it fell
    b.played = b.cn;
  }
  upsert(p: PlayerInfo) {
    const kind = p.creature ?? "alien"; let b = this.bodies.get(p.id);
    const fx = p.pos.x, fy = p.pos.y - BODY_TO_FEET, fz = p.pos.z;
    if (!b) {
      const root = new TransformNode(`cr_${p.id}`, this.scene); root.position.set(fx, fy, fz); root.rotation.y = p.yaw;
      b = { kind, root, inst: null, meshes: [], groups: new Map(), cur: "", from: null, fade: 1, target: new Vector3(fx, fy, fz), prev: new Vector3(fx, fy, fz), yaw: p.yaw, targetYaw: p.yaw,
        act: p.cact ?? "idle", cn: p.cn ?? 0, played: p.cn ?? 0, dead: !!p.dead, speed: 0, shown: true, fogHidden: false, track: new Track() };
      this.bodies.set(p.id, b);
      const c = this.model(kind); if (c) this.dress(p.id, b, c);
    }
    const tl = b.track.latest();                 // near where its poses have it: they carry on drawing it (this is untimed)
    if (!tl || Math.hypot(fx - tl.x, fy - tl.y, fz - tl.z) >= 1) { b.target.set(fx, fy, fz); b.targetYaw = p.yaw; }
    b.act = p.dead ? "die" : p.cact ?? b.act; b.cn = p.cn ?? b.cn; b.dead = !!p.dead;
  }
  pose(id: string, x: number, y: number, z: number, yaw: number, at?: number, interval = 1000 / 15, now = NaN) { const b = this.bodies.get(id); if (!b) return; b.target.set(x, y - BODY_TO_FEET, z); b.targetYaw = yaw; if (at !== undefined) b.track.push(at, x, y - BODY_TO_FEET, z, yaw, interval, now); }
  drop(id: string) { const b = this.bodies.get(id); if (!b) return; b.inst?.dispose(); b.root.dispose(false, false); this.bodies.delete(id); }   // its own skeleton and animations go with it (the materials are shared)
  clear() { for (const id of [...this.bodies.keys()]) this.drop(id); }
  // the overhead's fog: a creature in the dark isn't seen (but can still be shot)
  fog(hidden: (x: number, z: number) => boolean) {
    for (const b of this.bodies.values()) { const h = hidden(b.root.position.x, b.root.position.z); if (h !== b.fogHidden) { b.fogHidden = h; for (const m of b.meshes) m.isVisible = !h; } }
  }

  // ---------- every frame ----------
  update(dt: number, cam: Vector3 | null, poseAt = NaN) {
    const k = 1 - Math.exp(-dt * 12), kt = Math.min(1, dt * 8), at = { x: 0, y: 0, z: 0, yaw: 0 };
    for (const b of this.bodies.values()) {
      const near = !cam || (Vector3.DistanceSquared(cam, b.target) < NEAR * NEAR && Math.abs(cam.y - b.target.y) < 7);
      if (near !== b.shown) { b.shown = near; b.root.setEnabled(near); }
      b.prev.copyFrom(b.root.position);
      const tl = b.track.latest(), onTrack = !!tl && tl.x === b.target.x && tl.y === b.target.y && tl.z === b.target.z;
      if (tl && !onTrack) b.track.clear();      // placed somewhere else since: start again from its next pose
      if (onTrack && b.track.at(poseAt, at)) { b.root.position.set(at.x, at.y, at.z); b.targetYaw = at.yaw; }   // its newest place is the track's
      else if (Vector3.DistanceSquared(b.root.position, b.target) > 16) b.root.position.copyFrom(b.target); else Vector3.LerpToRef(b.root.position, b.target, k, b.root.position);
      let d = b.targetYaw - b.yaw; d = Math.atan2(Math.sin(d), Math.cos(d)); b.yaw += d * kt; b.root.rotation.y = b.yaw;
      if (!near || !b.inst) continue;
      const mv = Math.hypot(b.root.position.x - b.prev.x, b.root.position.z - b.prev.z) / Math.max(dt, 1e-3); b.speed += (mv - b.speed) * Math.min(1, dt * 6);
      this.animate(b, dt);
    }
  }
  // what to play: the death (once, held), a new one-shot (the attack, the flinch), one still playing, else the gait for the pace
  private animate(b: Body, dt: number) {
    const m = MODELS[b.kind], cur = b.groups.get(b.cur);
    let next: string, ratio = 1, restart = false;
    if (b.dead) { if (b.cur === "die") { this.fadeTick(b, dt); return; } next = "die"; }
    else if (b.cn !== b.played && (b.act === "attack" || b.act === "hit")) { next = b.act; restart = true; b.played = b.cn; }
    else if (b.cur && !LOOPS.has(b.cur) && cur?.isPlaying) { this.fadeTick(b, dt); return; }
    else if (b.act === "run" || b.speed > 3.0) { next = "run"; ratio = Math.min(1.6, Math.max(0.6, b.speed / m.run || 1)); }
    else if (b.act === "walk" || b.speed > 0.3) { next = "walk"; ratio = Math.min(2.0, Math.max(0.5, b.speed / m.walk || 1)); }
    else next = "idle";
    if (next === b.cur && !restart) { if (cur && LOOPS.has(next)) cur.speedRatio = ratio; this.fadeTick(b, dt); return; }
    const ng = b.groups.get(next); if (!ng) return;
    if (b.from && b.from !== ng) { b.from.stop(); b.from = null; }
    if (cur && cur !== ng && cur.isPlaying) { b.from = cur; b.fade = 0; } else if (cur && cur !== ng) cur.stop();   // fade out of what's still moving (a finished one-shot holds its pose)
    if (cur === ng) ng.stop();
    const loop = LOOPS.has(next); ng.start(loop, loop ? ratio : 1, ng.from, ng.to);
    ng.setWeightForAllAnimatables(b.from ? 0 : 1); b.cur = next;
    this.fadeTick(b, dt);
  }
  private fadeTick(b: Body, dt: number) {
    if (!b.from) return;
    b.fade = Math.min(1, b.fade + dt / FADE_S);
    b.groups.get(b.cur)?.setWeightForAllAnimatables(b.fade); b.from.setWeightForAllAnimatables(1 - b.fade);
    if (b.fade >= 1) { b.from.stop(); b.from = null; }
  }
}
